import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  UIEventHandler,
  WheelEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { VirtualElement } from "@popperjs/core";
import { useDebouncedCallback } from "use-debounce";
import { Variable } from "../../models/interactions/Variable";
import { registerPendingTextEdit } from "../../utils/pendingTextEdits";
import {
  convertDisplayToStoredTokens,
  convertStoredToDisplayTokens,
  convertTokenSyntaxToPlainVariableNames,
  tokenizeVariableString,
  VariableTokenIssue,
  VariableTokenOptions,
} from "./VariableTokenUtils";
import { getTextareaCaretClientRect } from "./textareaCaretPosition";
import useVariableIntellisense from "./useVariableIntellisense.hook";

interface UseVariableTextareaControllerOptions extends VariableTokenOptions {
  value: string;
  onCommit: (nextValue: string) => void;
  variables: Variable[];
  debounceMs?: number;
  enableVariableIntellisense?: boolean;
  flushOnCleanupKeys?: readonly unknown[];
}

function getWarningText(issues: VariableTokenIssue[]): string {
  const uniqueIssues = Array.from(new Set(issues.map((issue) => issue.tokenText)));
  if (uniqueIssues.length === 0) return "";
  if (uniqueIssues.length === 1) {
    return `Unknown variable: ${uniqueIssues[0]}`;
  }
  return `Unknown variables: ${uniqueIssues.join(", ")}`;
}

export default function useVariableTextareaController({
  value,
  onCommit,
  variables,
  debounceMs = 500,
  allowVariables = true,
  checkVariables = true,
  enableVariableIntellisense = true,
  flushOnCleanupKeys = [],
}: UseVariableTextareaControllerOptions) {
  const [editableValue, setEditableValue] = useState("");
  const [issues, setIssues] = useState<VariableTokenIssue[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [caretRect, setCaretRect] = useState<DOMRect | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const latestEditableValueRef = useRef("");
  const latestCommitRef = useRef(onCommit);
  const variablesRef = useRef(variables);
  const pendingCleanupRef = useRef<(() => void) | null>(null);
  const hasPendingRef = useRef(false);
  const optionsRef = useRef({
    allowVariables,
    checkVariables,
  });

  useEffect(() => {
    latestCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  useEffect(() => {
    optionsRef.current = {
      allowVariables,
      checkVariables,
    };
  }, [allowVariables, checkVariables]);

  useEffect(() => {
    latestEditableValueRef.current = editableValue;
  }, [editableValue]);

  // A scrollbar narrows the textarea's text box but not the overlay's, so the
  // two would wrap long text at different points. Track its width so the
  // overlay can give up the same space.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || typeof ResizeObserver === "undefined") return;

    const measure = () => setScrollbarWidth(measureScrollbarWidth(textarea));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(textarea);
    return () => observer.disconnect();
  }, []);

  const storedToDisplay = useMemo(
    () =>
      convertStoredToDisplayTokens(value, variables, {
        allowVariables,
        checkVariables,
      }),
    [value, variables, allowVariables, checkVariables]
  );

  useEffect(() => {
    if (isFocused) return;
    setEditableValue(storedToDisplay.value);
    latestEditableValueRef.current = storedToDisplay.value;
    setIssues(storedToDisplay.issues);
  }, [storedToDisplay.value, storedToDisplay.issues, isFocused]);

  const clearPendingFlush = () => {
    pendingCleanupRef.current?.();
    pendingCleanupRef.current = null;
    hasPendingRef.current = false;
  };

  const persistDisplayValue = (nextDisplayValue: string) => {
    const displayToStored = convertDisplayToStoredTokens(
      nextDisplayValue,
      variablesRef.current,
      optionsRef.current
    );

    setIssues(displayToStored.issues);
    latestCommitRef.current(displayToStored.value);
    clearPendingFlush();
  };

  const debouncedPersist = useDebouncedCallback((nextDisplayValue: string) => {
    persistDisplayValue(nextDisplayValue);
  }, debounceMs);

  const persistImmediateValue = (nextDisplayValue: string) => {
    latestEditableValueRef.current = nextDisplayValue;
    debouncedPersist.cancel?.();
    clearPendingFlush();
    persistDisplayValue(nextDisplayValue);
  };

  const flushPendingValue = () => {
    if (!hasPendingRef.current) {
      return;
    }

    debouncedPersist.flush?.();

    if (hasPendingRef.current) {
      persistDisplayValue(latestEditableValueRef.current);
    }
  };

  const registerPendingFlush = () => {
    pendingCleanupRef.current?.();
    pendingCleanupRef.current = registerPendingTextEdit(flushPendingValue);
    hasPendingRef.current = true;
  };

  useEffect(() => {
    return () => {
      flushPendingValue();
      clearPendingFlush();
    };
  }, [debouncedPersist, ...flushOnCleanupKeys]);

  const highlightedSegments = useMemo(() => {
    if (!allowVariables) {
      return [{ type: "text" as const, text: editableValue }];
    }

    const tokenized = tokenizeVariableString(editableValue, variables);

    if (isFocused) {
      return tokenized;
    }

    return tokenized.map((segment) => {
      if (segment.type === "token" && segment.isKnown && segment.variable?.name) {
        return {
          ...segment,
          text: segment.variable.name,
        };
      }

      return segment;
    });
  }, [editableValue, variables, allowVariables, isFocused]);

  const nonEditDisplay = useMemo(
    () =>
      convertTokenSyntaxToPlainVariableNames(editableValue, variables, {
        allowVariables,
        checkVariables,
      }),
    [editableValue, variables, allowVariables, checkVariables]
  );

  const inputValue = isFocused ? editableValue : nonEditDisplay.value;
  const warningText = useMemo(() => getWarningText(issues), [issues]);

  const {
    isSuggestionOpen,
    selectedSuggestionIndex,
    filteredSuggestions,
    suggestionMenuRef,
    closeSuggestionMenu,
    updateSuggestionState,
    insertVariable,
    handleKeyDown: handleIntellisenseKeyDown,
  } = useVariableIntellisense({
    enabled: enableVariableIntellisense,
    variables,
    editableValue,
    setEditableValue,
    textareaRef,
    persistValue: persistImmediateValue,
  });

  const handleFocus: FocusEventHandler<HTMLTextAreaElement> = () => {
    setIsFocused(true);
    const textarea = textareaRef.current;
    if (!textarea) return;
    setCaretRect(
      getTextareaCaretClientRect(textarea, textarea.selectionStart ?? textarea.value.length)
    );
  };

  const syncTextareaScroll = (
    textarea: HTMLTextAreaElement | null | undefined
  ) => {
    if (!(textarea instanceof HTMLTextAreaElement)) {
      return;
    }

    setScrollTop(textarea.scrollTop);
    setScrollLeft(textarea.scrollLeft);
    setScrollbarWidth(measureScrollbarWidth(textarea));
  };

  const syncTextareaScrollAfterLayout = (
    textarea: HTMLTextAreaElement | null | undefined
  ) => {
    if (!(textarea instanceof HTMLTextAreaElement)) {
      return;
    }

    requestAnimationFrame(() => {
      if (textareaRef.current !== textarea) {
        return;
      }

      syncTextareaScroll(textarea);
      updateCaretRect(textarea, textarea.selectionStart);
    });
  };

  const updateCaretRect = (
    textarea: HTMLTextAreaElement | null | undefined,
    selectionStart: number | null | undefined
  ) => {
    if (!(textarea instanceof HTMLTextAreaElement)) {
      return;
    }

    const caretPosition = selectionStart ?? textarea.value.length;
    setCaretRect(getTextareaCaretClientRect(textarea, caretPosition));
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const nextValue = e.target.value;
    latestEditableValueRef.current = nextValue;
    setEditableValue(nextValue);
    registerPendingFlush();
    debouncedPersist(nextValue);
    updateSuggestionState(nextValue, e.target.selectionStart);
    syncTextareaScroll(e.target);
    updateCaretRect(e.target, e.target.selectionStart);
    syncTextareaScrollAfterLayout(e.target);
  };

  const handleBlur: FocusEventHandler<HTMLTextAreaElement> = (e) => {
    const nextEditableValue = e.target.value;
    latestEditableValueRef.current = nextEditableValue;
    flushPendingValue();

    const normalizedDisplay = convertStoredToDisplayTokens(
      convertDisplayToStoredTokens(
        nextEditableValue,
        variablesRef.current,
        optionsRef.current
      ).value,
      variablesRef.current,
      optionsRef.current
    );

    setEditableValue(normalizedDisplay.value);
    latestEditableValueRef.current = normalizedDisplay.value;
    setIssues(normalizedDisplay.issues);
    setIsFocused(false);
    setCaretRect(null);
    closeSuggestionMenu();
  };

  const handleClick: MouseEventHandler<HTMLTextAreaElement> = (e) => {
    updateSuggestionState(e.currentTarget.value, e.currentTarget.selectionStart);
    updateCaretRect(e.currentTarget, e.currentTarget.selectionStart);
  };

  const handleKeyUp: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (
      e.key === "ArrowDown" ||
      e.key === "ArrowUp" ||
      e.key === "Enter" ||
      e.key === "Escape"
    ) {
      return;
    }

    updateSuggestionState(e.currentTarget.value, e.currentTarget.selectionStart);
    updateCaretRect(e.currentTarget, e.currentTarget.selectionStart);
  };

  const handleScroll: UIEventHandler<HTMLTextAreaElement> = (e) => {
    syncTextareaScroll(e.currentTarget);
    updateCaretRect(e.currentTarget, e.currentTarget.selectionStart);
  };

  const handleWheel: WheelEventHandler<HTMLTextAreaElement> = () => {};

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    handleIntellisenseKeyDown(e);
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      syncTextareaScroll(textarea);
      updateCaretRect(textarea, textarea.selectionStart);
    });
  };

  const caretAnchorEl: VirtualElement | null = useMemo(() => {
    if (!caretRect) return null;

    return {
      contextElement: textareaRef.current ?? undefined,
      getBoundingClientRect: () => caretRect,
    };
  }, [caretRect]);

  return {
    textareaRef,
    editableValue,
    inputValue,
    issues,
    warningText,
    highlightedSegments,
    scrollTop,
    scrollLeft,
    scrollbarWidth,
    isFocused,
    flushPendingValue,
    handleFocus,
    handleChange,
    handleBlur,
    handleClick,
    handleKeyUp,
    handleScroll,
    handleWheel,
    handleKeyDown,
    suggestions: {
      open:
        enableVariableIntellisense &&
        isFocused &&
        isSuggestionOpen &&
        filteredSuggestions.length > 0,
      anchorEl: caretAnchorEl ?? textareaRef.current,
      selectedIndex: selectedSuggestionIndex,
      filteredSuggestions,
      menuRef: suggestionMenuRef,
      onSelect: insertVariable,
    },
  };
}

function measureScrollbarWidth(textarea: HTMLTextAreaElement): number {
  const style = window.getComputedStyle(textarea);
  const borders =
    (parseFloat(style.borderLeftWidth) || 0) +
    (parseFloat(style.borderRightWidth) || 0);
  return Math.max(0, textarea.offsetWidth - textarea.clientWidth - borders);
}
